/**
 * Transform flat lead/opportunity data to nested structure
 */
export const transformSalesData = (item) => {
  const transformed = { ...item };

  // For SalesLead, extract data from lead_data JSONField
  if (item.lead_data) {
    // Extract basic job-level fields
    transformed.job_title = item.lead_data.job_title;
    transformed.company = item.lead_data.company;
    transformed.location = item.lead_data.location || item.lead_data.short_location;
    transformed.short_location = item.lead_data.short_location;
    transformed.url = item.lead_data.url;
    transformed.date_posted = item.lead_data.date_posted;
    transformed.date_created = item.lead_data.date_created;
    transformed.date_validthrough = item.lead_data.date_validthrough;
    transformed.description = item.lead_data.description;
    transformed.employment_type = item.lead_data.employment_type;
    transformed.remote = item.lead_data.remote;

    // Extract salary fields
    transformed.salary_string = item.lead_data.salary_string;
    transformed.min_salary = item.lead_data.min_salary;
    transformed.max_salary = item.lead_data.max_salary;
    transformed.currency = item.lead_data.currency;

    // Extract source fields
    transformed.source = item.lead_data.source;
    transformed.source_domain = item.lead_data.source_domain;
    transformed.source_type = item.lead_data.source_type;

    // Extract locations array
    if (item.lead_data.locations) {
      transformed.locations = item.lead_data.locations;
    }

    // Extract ALL AI-enriched fields
    transformed.ai_salary_currency = item.lead_data.ai_salary_currency;
    transformed.ai_salary_value = item.lead_data.ai_salary_value;
    transformed.ai_salary_minvalue = item.lead_data.ai_salary_minvalue;
    transformed.ai_salary_maxvalue = item.lead_data.ai_salary_maxvalue;
    transformed.ai_salary_unittext = item.lead_data.ai_salary_unittext;
    transformed.ai_benefits = item.lead_data.ai_benefits;
    transformed.ai_experience_level = item.lead_data.ai_experience_level;
    transformed.ai_work_arrangement = item.lead_data.ai_work_arrangement;
    transformed.ai_work_arrangement_office_days = item.lead_data.ai_work_arrangement_office_days;
    transformed.ai_remote_location = item.lead_data.ai_remote_location;
    transformed.ai_remote_location_derived = item.lead_data.ai_remote_location_derived;
    transformed.ai_key_skills = item.lead_data.ai_key_skills;
    transformed.ai_hiring_manager_name = item.lead_data.ai_hiring_manager_name;
    transformed.ai_hiring_manager_email_address = item.lead_data.ai_hiring_manager_email_address;
    transformed.ai_core_responsibilities = item.lead_data.ai_core_responsibilities;
    transformed.ai_requirements_summary = item.lead_data.ai_requirements_summary;
    transformed.ai_working_hours = item.lead_data.ai_working_hours;
    transformed.ai_employment_type = item.lead_data.ai_employment_type;
    transformed.ai_job_language = item.lead_data.ai_job_language;
    transformed.ai_visa_sponsorship = item.lead_data.ai_visa_sponsorship;
    transformed.ai_taxonomies_a = item.lead_data.ai_taxonomies_a;
    transformed.ai_keywords = item.lead_data.ai_keywords;
    transformed.ai_education_requirements = item.lead_data.ai_education_requirements;
    transformed.ai_has_salary = item.lead_data.ai_has_salary;

    // Extract ALL LinkedIn company data
    transformed.linkedin_id = item.lead_data.linkedin_id;
    transformed.linkedin_org_employees = item.lead_data.linkedin_org_employees;
    transformed.linkedin_org_url = item.lead_data.linkedin_org_url;
    transformed.linkedin_org_size = item.lead_data.linkedin_org_size;
    transformed.linkedin_org_slogan = item.lead_data.linkedin_org_slogan;
    transformed.linkedin_org_industry = item.lead_data.linkedin_org_industry;
    transformed.linkedin_org_followers = item.lead_data.linkedin_org_followers;
    transformed.linkedin_org_headquarters = item.lead_data.linkedin_org_headquarters;
    transformed.linkedin_org_type = item.lead_data.linkedin_org_type;
    transformed.linkedin_org_foundeddate = item.lead_data.linkedin_org_foundeddate;
    transformed.linkedin_org_specialties = item.lead_data.linkedin_org_specialties;
    transformed.linkedin_org_locations = item.lead_data.linkedin_org_locations;
    transformed.linkedin_org_description = item.lead_data.linkedin_org_description;
    transformed.linkedin_org_recruitment_agency_derived = item.lead_data.linkedin_org_recruitment_agency_derived;
    transformed.linkedin_org_slug = item.lead_data.linkedin_org_slug;

    // Use company_object from lead_data if it exists
    if (item.lead_data.company_object) {
      transformed.company_object = item.lead_data.company_object;
    }
  }

  // For SalesOpportunity, opportunity_data fields are already extracted by serializer
  // No additional processing needed since SerializerMethodFields handle it

  // Create company_object from company_* fields (fallback or for opportunities without nested data)
  if (!transformed.company_object) {
    transformed.company_object = {
      name: item.company_name || item.company,
      domain: item.company_domain,
      url: item.company_url,
      industry: item.company_industry,
      employee_count: item.company_employee_count,
      employee_count_range: item.company_employee_count_range,
      logo: item.company_logo,
      linkedin_url: item.company_linkedin_url,
      city: item.company_city,
      postal_code: item.company_postal_code,
      founded_year: item.company_founded_year,
      long_description: item.company_long_description,
      seo_description: item.company_seo_description,
      annual_revenue_usd: item.company_annual_revenue_usd,
      annual_revenue_usd_readable: item.company_annual_revenue_usd_readable,
      total_funding_usd: item.company_total_funding_usd,
      funding_stage: item.company_funding_stage,
      last_funding_round_date: item.company_last_funding_round_date,
      last_funding_round_amount_readable: item.company_last_funding_round_amount_readable,
      num_jobs: item.company_num_jobs,
      num_jobs_last_30_days: item.company_num_jobs_last_30_days,
      num_technologies: item.company_num_technologies,
    };
  }

  // Create locations array from location_* fields
  if (item.location_name || item.location_city) {
    transformed.locations = [{
      name: item.location_name,
      display_name: item.location_display_name,
      city: item.location_city,
      state: item.location_state,
      state_code: item.location_state_code,
      country_name: item.location_country_name,
      country_code: item.location_country_code,
      latitude: item.location_latitude,
      longitude: item.location_longitude,
    }];
  }

  // Create hiring_team array from hiring_* fields
  if (item.hiring_team_first_name || item.hiring_team_full_name) {
    transformed.hiring_team = [{
      first_name: item.hiring_team_first_name,
      full_name: item.hiring_team_full_name,
      role: item.hiring_team_role,
      linkedin_url: item.hiring_team_linkedin_url,
      image_url: item.hiring_team_image_url,
    }];
  }

  // Initialize outreach_log as empty array if not present
  if (!transformed.outreach_log) {
    transformed.outreach_log = [];
  }

  return transformed;
};

/**
 * Create mutable deep copy for AG Grid (which mutates objects during editing)
 */
export const createMutableCopy = (item) => {
  const copy = { ...item };

  // Copy nested objects if they exist
  if (item.company_object) {
    copy.company_object = { ...item.company_object };
  }
  if (item.locations) {
    copy.locations = item.locations.map(loc => ({ ...loc }));
  }
  if (item.hiring_team) {
    copy.hiring_team = item.hiring_team.map(member => ({ ...member }));
  }
  if (item.outreach_log) {
    copy.outreach_log = item.outreach_log.map(entry => ({ ...entry }));
  }
  if (item.contact_status_detail) {
    copy.contact_status_detail = { ...item.contact_status_detail };
  }
  if (item.priority_detail) {
    copy.priority_detail = { ...item.priority_detail };
  }

  return copy;
};

/**
 * Build bidirectional lookup maps for statuses and priorities
 */
export const buildLookupMaps = (contactStatuses, priorities) => {
  const statusNameToId = {};
  const statusIdToName = {};
  const statusNames = [];
  contactStatuses.forEach(status => {
    statusNameToId[status.name] = status.id;
    statusIdToName[status.id] = status.name;
    statusNames.push(status.name);
  });

  const priorityNameToId = {};
  const priorityIdToName = {};
  const priorityNames = [];
  priorities.forEach(priority => {
    priorityNameToId[priority.name] = priority.id;
    priorityIdToName[priority.id] = priority.name;
    priorityNames.push(priority.name);
  });

  return {
    statusNameToId,
    statusIdToName,
    statusNames,
    priorityNameToId,
    priorityIdToName,
    priorityNames
  };
};
